<script>
  
  import { onMount, onDestroy } from "svelte";
	import { Dispatcher} from "../../Dispatcher.js";
	import { decoder_thread_ready, awaiting_keyframe } from "../../stores.js";
  import Loader from "./Loader.svelte";
  
  let canvas;
  let canvasTransferred = false;
  let ro;
  let dprQuery = null;
  let pending = null;
  let raf = 0;

  // -- at most one resize per animation frame (a window drag fires the observer repeatedly)
  function sendSize(height, width) {
    pending = {height, width};
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      if (pending) Dispatcher.updatePlaybackCanvasSize(pending.height, pending.width);
      pending = null;
    });
  }

  function notifySize() {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    sendSize(rect.height * dpr, rect.width * dpr);
  }

  // -- without device-pixel-content-box (Safari), moving to a display with another pixel ratio
  // -- changes nothing in CSS pixels: watch the ratio itself
  function onDprChange() {
    notifySize();
    watchDpr();
  }

  function watchDpr() {
    dprQuery && dprQuery.removeEventListener("change", onDprChange);
    dprQuery = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    dprQuery.addEventListener("change", onDprChange);
  }

  $: if (canvas && $decoder_thread_ready && !canvasTransferred) {
    const didTransfer = Dispatcher.transferPlaybackCanvas(canvas);
    if (didTransfer) {
      canvasTransferred = true;
      notifySize();
    }
  }

  onMount(() => {
    // -- The backing store must be sized in device pixels, not CSS pixels: otherwise the
    // -- renderer downscales 1080p frames to the CSS size and the compositor upscales them
    // -- back on HiDPI displays. device-pixel-content-box reports exact device pixels and
    // -- fires on a DPR change; Safari doesn't support it, hence rect * dpr plus a DPR watch.
    try {
      ro = new ResizeObserver(entries => {
        for (const entry of entries) {
          const dpx = entry.devicePixelContentBoxSize?.[0];
          if (dpx) sendSize(dpx.blockSize, dpx.inlineSize);
          else notifySize();
        }
      });
      ro.observe(canvas, { box: 'device-pixel-content-box' });
    } catch {
      ro = new ResizeObserver(() => notifySize());
      ro.observe(canvas);
      watchDpr();
    }
  });

  onDestroy(() => {
    ro && ro.disconnect();
    raf && cancelAnimationFrame(raf);
    dprQuery && dprQuery.removeEventListener("change", onDprChange);
  });
  
  </script>
  <div id="canvas-section" class="pc-section">
    <div class="pc-wrapper">
      <!-- no CSS filter here, ever: the QuickTime gamma lives in the renderer's shader, and any
           filter on this element pulls the whole window off CoreAnimation delegation -->
      <canvas bind:this={canvas} id="pc"/>
      {#if $awaiting_keyframe}
        <div class="kf-loader">
          <Loader/>
        </div>
      {/if}
    </div>
  </div>
  <style lang="scss">
    
    @import "../styles/main.scss";

    .pc-section {

      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      position: relative;

    }

    .pc-wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      position: relative;
      width: 100%;
    }

    /* centered over the canvas (wrapper bounds match the canvas box) while the
       display is stalled waiting for a frame -- stream keyframe or cache decode */
    .kf-loader {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }

    canvas {

      display: block;
      
      /* Respect parent width and clamp by available max height */
      aspect-ratio: 16/9;
      height: auto;
      width: min(100%, calc(var(--pc-max-h, 2000px) * (16 / 9)));
      
      
      border-radius: $rounded-md;
      border: 0.1px solid rgba(109, 109, 109, 0.732);
      background-color: black;

    }


  </style>
  




  
